// components/SeoForm.tsx
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ROBOTS_OPTIONS, SeoFormData, SeoFormProps } from '../../interfaces/seo';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL } from '../../api/constant';
import { pageLinks } from '../../utility/helper';
export interface SeoFormRef {
    getFormData: () => SeoFormData;
    triggerValidation: () => Promise<boolean>;
}
const SeoForm = forwardRef<SeoFormRef, SeoFormProps>(({
    entityId,
    entityType,
    initialData,
    isRequired = false,
    isPagePath = false,
}, ref) => {
    const {
        register,
        control,
        watch,
        formState: { errors },
        reset,
        getValues,
        trigger,
    } = useForm<SeoFormData>({
        defaultValues: {
            entityId,
            entityType,
            pagePath: initialData?.pagePath || '',
            metaTitle: initialData?.metaTitle || '',
            metaDescription: initialData?.metaDescription || '',
            metaKeywords: initialData?.metaKeywords || '',
            ogTitle: initialData?.ogTitle || '',
            ogDescription: initialData?.ogDescription || '',
            ogImage: initialData?.ogImage || '',
            canonicalUrl: initialData?.canonicalUrl || '',
            robots: initialData?.robots || 'index, follow',
            schemaMarkup: initialData?.schemaMarkup || '',
        },
        mode: 'onChange',
    });

    // Reset form when entityId or entityType changes
    useEffect(() => {
        reset({
            entityId,
            entityType,
            pagePath: initialData?.pagePath || '',
            metaTitle: initialData?.metaTitle || '',
            metaDescription: initialData?.metaDescription || '',
            metaKeywords: initialData?.metaKeywords || '',
            ogTitle: initialData?.ogTitle || '',
            ogDescription: initialData?.ogDescription || '',
            ogImage: initialData?.ogImage || '',
            canonicalUrl: initialData?.canonicalUrl || '',
            robots: initialData?.robots || 'index, follow',
            schemaMarkup: initialData?.schemaMarkup || '',
        });
    }, [entityId, entityType, initialData, reset]);

    useImperativeHandle(ref, () => ({
        getFormData: () => getValues(),
        triggerValidation: () => trigger(),
    }));

    // Validate URL format
    const validateUrl = (value?: string) => {
        if (!value) return true; // Optional field
        try {
            new URL(value);
            return true;
        } catch {
            return 'Please enter a valid URL';
        }
    };

    // Validate max length
    const validateMaxLength = (max: number) => (value?: string) => {
        if (!value) return true;
        return value.length <= max || `Must be less than ${max} characters`;
    };

    return (
        <div className="seo-form card">
            <div className="card-header">
                <h3 className="card-title h5 mb-0">SEO Settings</h3>
            </div>
            <div className="card-body">
                <div className="row g-3">
                    {/* Hidden fields for entityId and entityType */}
                    <input type="hidden" {...register('entityId')} />
                    <input type="hidden" {...register('entityType')} />

                    {/* Page Path */}
                    {isPagePath && <div className="col-12">
                        <label htmlFor="pagePath" className="form-label">
                            Page Path {isRequired && <span className="text-danger">*</span>}
                        </label>
                        <select
                            id="pagePath"
                            className={`form-select ${errors.pagePath ? 'is-invalid' : ''}`}
                            {...register('pagePath', {
                                required: isRequired ? 'Page path is required' : false,
                            })}
                        >
                            <option value="">Select Page Path</option>
                            {
                                pageLinks.map((page) => (
                                    <option key={page.value} value={page.value}>
                                        {page.label}
                                    </option>
                                ))
                            }
                        </select>
                        {errors.pagePath && (
                            <div className="invalid-feedback">{errors.pagePath.message}</div>
                        )}
                    </div>
                    }
                    {/* Meta Title */}
                    <div className="col-12">
                        <label htmlFor="metaTitle" className="form-label">
                            Meta Title {isRequired && <span className="text-danger">*</span>}
                        </label>
                        <input
                            type="text"
                            id="metaTitle"
                            className={`form-control ${errors.metaTitle ? 'is-invalid' : ''}`}
                            placeholder="The Meatery - Buy Fresh Meat Online"
                            {...register('metaTitle', {
                                required: isRequired ? 'Meta title is required' : false,
                                validate: validateMaxLength(70),
                            })}
                        />
                        <div className="form-text d-flex justify-content-between">
                            <span>Recommended: 50-60 characters</span>
                            <span>{watch('metaTitle')?.length || 0}/60</span>
                        </div>
                        {errors.metaTitle && (
                            <div className="invalid-feedback">{errors.metaTitle.message}</div>
                        )}
                    </div>

                    {/* Meta Description */}
                    <div className="col-12">
                        <label htmlFor="metaDescription" className="form-label">
                            Meta Description {isRequired && <span className="text-danger">*</span>}
                        </label>
                        <textarea
                            id="metaDescription"
                            rows={3}
                            className={`form-control ${errors.metaDescription ? 'is-invalid' : ''}`}
                            placeholder="Order premium fresh meat online. Same-day delivery."
                            {...register('metaDescription', {
                                required: isRequired ? 'Meta description is required' : false,
                                validate: validateMaxLength(180),
                            })}
                        />
                        <div className="form-text d-flex justify-content-between">
                            <span>Recommended: 150-160 characters</span>
                            <span>{watch('metaDescription')?.length || 0}/160</span>
                        </div>
                        {errors.metaDescription && (
                            <div className="invalid-feedback">{errors.metaDescription.message}</div>
                        )}
                    </div>

                    {/* Meta Keywords */}
                    <div className="col-12">
                        <label htmlFor="metaKeywords" className="form-label">
                            Meta Keywords
                        </label>
                        <input
                            type="text"
                            id="metaKeywords"
                            className="form-control"
                            placeholder="buy meat, halal beef, lamb online"
                            {...register('metaKeywords')}
                        />
                        <div className="form-text">Comma-separated keywords</div>
                    </div>

                    {/* OG Title */}
                    <div className="col-md-6">
                        <label htmlFor="ogTitle" className="form-label">
                            OG Title
                        </label>
                        <input
                            type="text"
                            id="ogTitle"
                            className={`form-control ${errors.ogTitle ? 'is-invalid' : ''}`}
                            placeholder="The Meatery - Premium Online Meat Store"
                            {...register('ogTitle', {
                                validate: validateMaxLength(70),
                            })}
                        />
                        {errors.ogTitle && (
                            <div className="invalid-feedback">{errors.ogTitle.message}</div>
                        )}
                    </div>

                    {/* OG Description */}
                    <div className="col-md-6">
                        <label htmlFor="ogDescription" className="form-label">
                            OG Description
                        </label>
                        <textarea
                            id="ogDescription"
                            rows={3}
                            className={`form-control ${errors.ogDescription ? 'is-invalid' : ''}`}
                            placeholder="Shop fresh halal meat online. Fast delivery in Bahrain."
                            {...register('ogDescription', {
                                validate: validateMaxLength(180),
                            })}
                        />
                        {errors.ogDescription && (
                            <div className="invalid-feedback">{errors.ogDescription.message}</div>
                        )}
                    </div>

                    {/* OG Image */}
                    <div className="col-6">
                        <label htmlFor="ogImage" className="form-label">
                            OG Image URL
                        </label>
                        <input
                            type="url"
                            id="ogImage"
                            className={`form-control ${errors.ogImage ? 'is-invalid' : ''}`}
                            placeholder="https://your-cdn.com/images/og-image.jpg"
                            {...register('ogImage', {
                                validate: validateUrl,
                            })}
                        />
                        {errors.ogImage && (
                            <div className="invalid-feedback">{errors.ogImage.message}</div>
                        )}
                    </div>

                    {/* Canonical URL */}
                    <div className="col-6">
                        <label htmlFor="canonicalUrl" className="form-label">
                            Canonical URL
                        </label>
                        <input
                            type="url"
                            id="canonicalUrl"
                            className={`form-control ${errors.canonicalUrl ? 'is-invalid' : ''}`}
                            placeholder="https://www.themeatery.com/products/beef-tenderloin"
                            {...register('canonicalUrl', {
                                validate: validateUrl,
                            })}
                        />
                        {errors.canonicalUrl && (
                            <div className="invalid-feedback">{errors.canonicalUrl.message}</div>
                        )}
                    </div>

                    {/* Robots */}
                    <div className="col-md-12">
                        <label htmlFor="robots" className="form-label">
                            Robots Directive
                        </label>
                        <Controller
                            name="robots"
                            control={control}
                            render={({ field }) => (
                                <select
                                    {...field}
                                    className="form-select"
                                >
                                    {ROBOTS_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            )}
                        />
                    </div>

                    {/* Schema Markup */}
                    <div className="col-12">
                        <label htmlFor="schemaMarkup" className="form-label">
                            Schema Markup (JSON-LD)
                        </label>
                        <textarea
                            id="schemaMarkup"
                            rows={3}
                            className="form-control font-monospace"
                            placeholder='{"@context": "https://schema.org", ...}'
                            {...register('schemaMarkup')}
                        />
                        <div className="form-text">JSON-LD structured data</div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default SeoForm;

export const submitSEOData = async (seoData: SeoFormData, id?: string | null) => {
    console.log('Submitting SEO Data:', seoData, 'ID:', id);
    try {
        const cleanedData = Object.fromEntries(
            Object.entries(seoData).filter(([_, value]) => value !== null && value !== '' && value !== undefined)
        );

        const url = id
            ? `${API_URL + API_ENDPOINT.page}seo/${id}`
            : `${API_URL + API_ENDPOINT.page}seo`;

        const method = id ? 'PATCH' : 'POST';

        const response = await callApi(url, {
            method,
            data: cleanedData,
        });

        return response;
    } catch (error) {
        console.error('Error submitting SEO data:', error);
        throw error;
    }
};

